import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../../../../supabaseClient";

import ServiceInformation from "./serviceInformation";
import Availability from "./availability";
import Location from "./location";
import Gallery from "./gallery";
import EditSeo from "./seo";
// ---- same types you defined for AddService ----
type AdditionalRow = { id: number; additionalService: number | null; price: number; duration: string, speciality: boolean };
type Option = { id: number; name: string };
type TagOption = { id: number; name: string };

type Info = {
  title: string;
  masterCategory: Option[];
  category: Option[];
  mainCategory: Option[];
  subCategory: Option[];
  tags: TagOption[];
  subTags: TagOption[];
  description: string;
  additionalEnabled: boolean;
  additional: AdditionalRow[];
  videoUrl: string;
  since?: string;
  accessibility?: string[];
  payment_options?: string[];
  service_mode?: string[];
  price_range?: string[];
  quality?: string[];
  sp_niche?: string[];
  cuisine?: string[];
  is_brand: boolean;       // ✅ add this
  brand_name: string | null; // ✅ add this
};

type Slot = { id: number; from: string; to: string; slots: string };
type PerDay = {
  monday: Slot[]; tuesday: Slot[]; wednesday: Slot[];
  thursday: Slot[]; friday: Slot[]; saturday: Slot[]; sunday: Slot[];
};
type DaySchedule = { closed: boolean; slots: Slot[] };

type AvailabilityT = {
  all: Slot[];
  perDay: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  };
};

type LocationT = { address?: string; lat?: number; lng?: number };
type GalleryT = { files: (File | { url: string })[] };
type SeoT = { slug: string; metaTitle?: string; metaDescription?: string; metaKeywords?: string[]; };

type ServiceForm = {
  info: Info;
  availability: AvailabilityT;
  location: LocationT;
  gallery: GalleryT;
  seo: SeoT;
};

const EditService = () => {
  const { id } = useParams<{ id: string }>(); // e.g. /edit-services/[id]
  const [form, setForm] = useState<ServiceForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.phone) {
        const dbMobile = session.user.phone.replace(/^\+?91/, '');
        const { data, error } = await supabase
          .from('rd_users')
          .select('*')
          .eq('mobile', dbMobile)
          .single();
        
        setUserRole(error ? null : data?.role || null);
      } else {
        //setProfile(null);
      }
    };
    fetchUser();
  }, []);
  // ---- Tab UI states ----
  const [PageChange, setPageChange] = useState<"information" | "booking" | "location" | "gallery" | "seo">("information");

  // ---- Load existing listing ----
  useEffect(() => {
    const fetchListing = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }
      const [
        cityRes,
        sectorsRes,
        mainCatsRes,
        subCatsRes,
        tagsRes,
        subTagsRes,
      ] = await Promise.all([
        data.city_id
          ? supabase.from("cities").select("id, category").in("id", data.city_id)
          : Promise.resolve({ data: [] }),
        data.sector_ids?.length
          ? supabase.from("sectors").select("id, category").in("id", data.sector_ids)
          : Promise.resolve({ data: [] }),
        data.main_category_ids?.length
          ? supabase.from("main_categories").select("id, category").in("id", data.main_category_ids)
          : Promise.resolve({ data: [] }),
        data.sub_category_ids?.length
          ? supabase.from("sub_categories").select("id, category").in("id", data.sub_category_ids)
          : Promise.resolve({ data: [] }),
        data.tag_ids?.length
          ? supabase.from("tags").select("id, category").in("id", data.tag_ids)
          : Promise.resolve({ data: [] }),
        data.sub_tag_ids?.length
          ? supabase.from("sub_tags").select("id, category").in("id", data.sub_tag_ids)
          : Promise.resolve({ data: [] }),
      ]);
      console.log(data);
      // Map DB → Form
      // normalize availability
      const normalizeAvailability = (raw: any): AvailabilityT => {
        if (!raw) {
          return {
            all: [],
            perDay: {
              monday: { closed: false, slots: [] },
              tuesday: { closed: false, slots: [] },
              wednesday: { closed: false, slots: [] },
              thursday: { closed: false, slots: [] },
              friday: { closed: false, slots: [] },
              saturday: { closed: false, slots: [] },
              sunday: { closed: false, slots: [] },
            },
          };
        }

        // if already has closed flag, return as is
        if (raw.perDay?.monday && "closed" in raw.perDay.monday) {
          return raw as AvailabilityT;
        }

        // fallback for older structure: wrap arrays
        const wrap = (arr: Slot[] = []): DaySchedule => ({ closed: false, slots: arr });

        return {
          all: raw.all || [],
          perDay: {
            monday: wrap(raw.perDay?.monday),
            tuesday: wrap(raw.perDay?.tuesday),
            wednesday: wrap(raw.perDay?.wednesday),
            thursday: wrap(raw.perDay?.thursday),
            friday: wrap(raw.perDay?.friday),
            saturday: wrap(raw.perDay?.saturday),
            sunday: wrap(raw.perDay?.sunday),
          },
        };
      };

      const mapped: ServiceForm = {
        info: {
          title: data.title,
          masterCategory: cityRes.data?.map((c) => ({ id: c.id, name: c.category })) || [],
          category: sectorsRes.data?.map((c) => ({ id: c.id, name: c.category })) || [],
          mainCategory: mainCatsRes.data?.map((c) => ({ id: c.id, name: c.category })) || [],
          subCategory: subCatsRes.data?.map((c) => ({ id: c.id, name: c.category })) || [],
          tags: tagsRes.data?.map((t) => ({ id: t.id, name: t.category })) || [],
          subTags: subTagsRes.data?.map((st) => ({ id: st.id, name: st.category })) || [],
          description: data.description || "",
          additionalEnabled: data.additional_enabled,
          additional: data.additional || [],
          videoUrl: data.video_url || "",
          since: data.since ?? null,
          accessibility: data.extra_details?.accessibility ?? [],
          payment_options: data.extra_details?.payment_options ?? [],
          service_mode: data.extra_details?.service_mode ?? [],
          price_range: data.extra_details?.price_range ?? [],
          quality: data.extra_details?.quality ?? [],
          sp_niche: data.extra_details?.sp_niche ?? [],
          cuisine: data.extra_details?.cuisine ?? [],
          is_brand: data.is_brand ?? false,
          brand_name: data.brand_name ?? null,

        },
        availability: normalizeAvailability(data.availability),
        location: { address: data.address, lat: data.lat, lng: data.lng },
        gallery: { files: (data.gallery_urls || []).map((url: string) => ({ url })) },
        seo: { slug: data.slug, metaTitle: data.meta_title || "", metaDescription: data.meta_description || "", metaKeywords: data.meta_keywords || "" }
      };
      setForm(mapped);
      setLoading(false);
    };

    if (id) fetchListing();
  }, [id]);

  // ---- Save updates ----
  const handleUpdate = async () => {
    if (!form) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      if (!form.info.title?.trim()) throw new Error('Title is required.');
      const uploadedUrls: string[] = [];
    for (const f of form.gallery.files) {
      if ("url" in f) {
        // already stored
        uploadedUrls.push(f.url);
      } else if (f instanceof File) {
        // upload new file
        const filePath = `services/${crypto.randomUUID()}-${f.name}`;
        const { error: uploadErr } = await supabase.storage
          .from("Service Gallery") // 👈 bucket name (make sure it matches yours)
          .upload(filePath, f, {
            cacheControl: "3600",
            upsert: false,
          });
        if (uploadErr) throw uploadErr;

        // get public URL
        const { data: publicUrlData } = supabase.storage
          .from("Service Gallery")
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrlData.publicUrl);
      }
    }
      const payload = {
        title: form.info.title,
        description: form.info.description,
        city_id: form.info.masterCategory.map(c => c.id),
        sector_ids: form.info.category.map(c => c.id),
        main_category_ids: form.info.mainCategory.map(c => c.id),
        sub_category_ids: form.info.subCategory.map(c => c.id),
        tag_ids: form.info.tags.map(c => c.id),
        sub_tag_ids: form.info.subTags.map(c => c.id),
        additional_enabled: form.info.additionalEnabled,
        additional: form.info.additional,
        video_url: form.info.videoUrl,
        since: form.info.since,
        availability: form.availability,
        address: form.location.address,
        lat: form.location.lat,
        lng: form.location.lng,
        gallery_urls: uploadedUrls,
        slug: form.seo.slug,
        meta_title: form.seo.metaTitle,
        meta_description: form.seo.metaDescription,
        meta_keywords: form.seo.metaKeywords,
        extra_details: {
          accessibility: form.info.accessibility ?? [],
          payment_options: form.info.payment_options ?? [],
          service_mode: form.info.service_mode ?? [],
          price_range: form.info.price_range ?? [],
          quality: form.info.quality ?? [],
          sp_niche: form.info.sp_niche ?? [],
          cuisine: form.info.cuisine ?? [],
        },
        is_brand: form.info.is_brand,
        brand_name: form.info.brand_name,

      };

      const { error } = await supabase
        .from("listings")
        .update(payload)
        .eq("id", id);

      if (error) throw error;

      alert("Listing updated successfully!");
      navigate("/services/all-services");
    } catch (e: any) {
      console.error(e);
      setErrorMsg(e.message ?? "Failed to update Listing.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) return <p>Loading...</p>;

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-lg-12 m-auto">
            <div className="progressbar-card">
              <ul id="progress-head">
                <li className="active">
                  Update Listing - Listing Information
                </li>
                <li>Add Listing - Availablity</li>
                <li>Add Listing - Location</li>
                <li>Add Listing - Gallery</li>
                <li>Add Listing - SEO</li>
              </ul>
              <ul id="progressbar">
                <li
                  className={PageChange === "information" ? "active" : ""}
                  onClick={() => setPageChange("information")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="multi-step-icon">
                    <span><i className="far fa-check-circle" /></span>
                  </div>
                  <div className="multi-step-info"><h6>Information</h6></div>
                </li>

                <li
                  className={PageChange === "booking" ? "active" : ""}
                  onClick={() => setPageChange("booking")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="multi-step-icon">
                    <span><i className="far fa-clock" /></span>
                  </div>
                  <div className="multi-step-info"><h6>Availability</h6></div>
                </li>

                <li
                  className={PageChange === "location" ? "active" : ""}
                  onClick={() => setPageChange("location")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="multi-step-icon">
                    <span><i className="far fa-map" /></span>
                  </div>
                  <div className="multi-step-info"><h6>Location</h6></div>
                </li>

                <li
                  className={PageChange === "gallery" ? "active" : ""}
                  onClick={() => setPageChange("gallery")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="multi-step-icon">
                    <span><i className="far fa-images" /></span>
                  </div>
                  <div className="multi-step-info"><h6>Gallery</h6></div>
                </li>

                {userRole === "A1" && (
                  <li
                    className={PageChange === "seo" ? "active" : ""}
                    onClick={() => setPageChange("seo")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="multi-step-icon">
                      <span><i className="far fa-chart-bar" /></span>
                    </div>
                    <div className="multi-step-info"><h6>SEO</h6></div>
                  </li>
                )}
              </ul>

            </div>
          </div>
        </div>
        {/* Error Message */}
        {errorMsg && (
          <div className="row">
            <div className="col-lg-12 m-auto">
              <div className="alert alert-danger">{errorMsg}</div>
            </div>
          </div>
        )}
        {/* --- Same tab UI as AddService --- */}
        {PageChange === "information" ? (
          <ServiceInformation value={form.info} onChange={(patch) => setForm(p => ({ ...p!, info: { ...p!.info, ...patch } }))} nextTab={() => setPageChange("booking")} onUpdate={handleUpdate} />
        ) : PageChange === "booking" ? (
          <Availability value={form.availability} onChange={(patch) => setForm(p => ({ ...p!, availability: { ...p!.availability, ...patch } }))} prevTab={() => setPageChange("information")} nextTab={() => setPageChange("location")} onUpdate={handleUpdate} />
        ) : PageChange === "location" ? (
          <Location value={form.location} onChange={(patch) => setForm(p => ({ ...p!, location: { ...p!.location, ...patch } }))} prevTab={() => setPageChange("booking")} nextTab={() => setPageChange("gallery")} onUpdate={handleUpdate} />
        ) : PageChange === "gallery" ? (
          //<Gallery value={form.gallery} onChange={(updater) => setForm(p => ({ ...p!, gallery: updater(p!.gallery) }))} prevTab={() => setPageChange("location")} nextTab={() => setPageChange("seo")} />
          <Gallery
            value={form.gallery}
            onChange={(patch) =>
              setForm((p) => ({
                ...p!, gallery: { ...p!.gallery, ...patch },
              }))
            }
            prevTab={() => setPageChange("location")}
            nextTab={() => {
              if (userRole === "A1") {
                setPageChange("seo");
              } else {
                handleUpdate();
              }
            }}
            onUpdate={handleUpdate}
            saving={saving}
            userRole={userRole}
          />

        ) : (
          userRole === "A1" && (
            <EditSeo value={form.seo} onChange={(patch) => setForm(p => ({ ...p!, seo: { ...p!.seo, ...patch } }))} prevTab={() => setPageChange("gallery")} onSave={handleUpdate} saving={saving} />
          ))}
      </div>
    </div>
  );
};

export default EditService;
